import { usePageTitle } from '../hooks/usePageTitle';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { fotoUrl } from '../api';
import { useTheme } from '../contexts/ThemeContext';

const Dashboard: React.FC = () => {
  usePageTitle('Gösterge Paneli');
  const navigate = useNavigate();
  const { isDark } = useTheme();

  const card        = isDark ? '#1a1a1a' : 'white';
  const cardAlt     = isDark ? '#1e1e1e' : '#f8f8f8';
  const cardHover   = isDark ? '#252525' : '#fafafa';
  const border      = isDark ? '#2a2a2a' : '#eee';
  const borderSub   = isDark ? '#202020' : '#f0f0f0';
  const textPrimary = isDark ? '#e8e8e8' : '#1a1a1a';
  const textSec     = isDark ? '#999'    : '#888';
  const textMuted   = isDark ? '#666'    : '#aaa';
  const progressBg  = isDark ? '#333'    : '#e8e8e8';
  const progressBg2 = isDark ? '#2a2a2a' : '#f0f0f0';

  const [veri, setVeri] = useState<any>(null);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [alarmlar, setAlarmlar] = useState<any[]>([]);
  const [teyidler, setTeyidler] = useState<any[]>([]);
  const [teyidDetay, setTeyidDetay] = useState<{ baslik: string; liste: any[] } | null>(null);
  const [teyidDetayYukleniyor, setTeyidDetayYukleniyor] = useState(false);
  const [gunPopup, setGunPopup] = useState<{ tarih: string; label: string } | null>(null);
  const [gunDersler, setGunDersler] = useState<any[]>([]);
  const [gunYukleniyor, setGunYukleniyor] = useState(false);

  useEffect(() => {
    api.get('/api/dashboard')
      .then(r => setVeri(r.data))
      .catch(err => console.error(err))
      .finally(() => setYukleniyor(false));
    api.get('/api/crm/alarmlar')
      .then(r => setAlarmlar(r.data))
      .catch(() => {});
    api.get('/api/katilim/admin/dashboard')
      .then(r => setTeyidler(r.data))
      .catch(() => {});
  }, []);

  const gunTikla = (gun: number, ay: number, yil: number) => {
    const tarih = `${yil}-${String(ay + 1).padStart(2, '0')}-${String(gun).padStart(2, '0')}`;
    const label = new Date(tarih + 'T12:00:00').toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', weekday: 'long' });
    setGunPopup({ tarih, label });
    setGunDersler([]);
    setGunYukleniyor(true);
    api.get(`/api/dashboard/gun/${tarih}`)
      .then(r => setGunDersler(r.data))
      .catch(() => setGunDersler([]))
      .finally(() => setGunYukleniyor(false));
  };

  const teyidDetayAc = async (t: any) => {
    const tarihStr = new Date(t.tarih).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', weekday: 'long' });
    setTeyidDetay({ baslik: `${t.ders_adi} — ${tarihStr}`, liste: [] });
    setTeyidDetayYukleniyor(true);
    try {
      const r = await api.get(`/api/katilim/ozet/${t.ders_id}`, { params: { tarih: t.tarih.slice(0, 10) } });
      setTeyidDetay({ baslik: `${t.ders_adi} — ${tarihStr}`, liste: r.data.liste || [] });
    } catch { setTeyidDetay(prev => prev ? { ...prev, liste: [] } : null); }
    finally { setTeyidDetayYukleniyor(false); }
  };

  const alarmTamamla = async (alarmId: string) => {
    try {
      await api.patch(`/api/crm/alarmlar/${alarmId}/tamamla`, { tamamlandi: true });
      setAlarmlar(p => p.filter(a => a.id !== alarmId));
    } catch (e) { console.error(e); }
  };

  const simdi = new Date();
  const ayAdi = simdi.toLocaleDateString('tr-TR', { month: 'long', year: 'numeric' });
  const bugunAy = simdi.getMonth();
  const bugunYil = simdi.getFullYear();
  const bugunGun = simdi.getDate();
  const ayinIlkGunu = new Date(bugunYil, bugunAy, 1).getDay();
  const ayinSonGunu = new Date(bugunYil, bugunAy + 1, 0).getDate();
  const GUNLER = ['Pzt','Sal','Çar','Per','Cum','Cmt','Paz'];
  const yoklamaGunleri = new Set((veri?.yoklama_gunleri || []).map((t: string) => new Date(t).getDate()));

  const gelirFarki = veri?.ozet?.gecen_ay_gelir > 0
    ? Math.round((veri.ozet.bu_ay_gelir - veri.ozet.gecen_ay_gelir) / veri.ozet.gecen_ay_gelir * 100)
    : 0;

  const RENKLER = ['#534AB7','#1D9E75','#E24B4A','#F5A623','#378ADD'];

  if (yukleniyor) return (
    <div style={{ padding:24, display:'flex', alignItems:'center', justifyContent:'center', height:'60vh', color: textMuted }}>
      Yukleniyor...
    </div>
  );

  return (
    <div style={{ padding:24, maxWidth:1200 }}>
      {/* Baslik */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
        <div>
          <h1 style={{ fontSize:22, fontWeight:600, margin:0, color: textPrimary }}>Genel bakis</h1>
          <div style={{ fontSize:13, color: textSec, marginTop:2 }}>
            {simdi.toLocaleDateString('tr-TR', { weekday:'long', day:'numeric', month:'long', year:'numeric' })}
          </div>
        </div>
        <span style={{ background: isDark ? 'rgba(83,74,183,0.2)' : '#EEEDFE', color:'#534AB7', borderRadius:20, padding:'6px 14px', fontSize:13, fontWeight:500 }}>
          {ayAdi}
        </span>
      </div>

      {/* Ozet kartlar */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:20 }}>
        {[
          {
            label:'Aktif ogrenci',
            value: veri?.ozet?.aktif_ogrenci || 0,
            alt: `Bu ay +${veri?.ozet?.bu_ay_yeni_ogrenci || 0}`,
            altRenk:'#3B6D11',
          },
          {
            label:'Bu haftaki dersler',
            value: veri?.ozet?.bu_hafta_dersler || 0,
            alt: `Bugun ${veri?.ozet?.bugun_ders_sayisi || 0} ders`,
            altRenk: isDark ? '#888' : '#555',
          },
          {
            label:`Gelir (${simdi.toLocaleDateString('tr-TR',{month:'long'})})`,
            value: `₺${(veri?.ozet?.bu_ay_gelir||0).toLocaleString('tr-TR')}`,
            alt: gelirFarki !== 0 ? `Gecen aya gore ${gelirFarki > 0 ? '+' : ''}${gelirFarki}%` : 'Ilk ay',
            altRenk: gelirFarki >= 0 ? '#3B6D11' : '#A32D2D',
          },
          {
            label:'Ort. katilim',
            value: `${veri?.ozet?.katilim_orani || 0}%`,
            alt: 'Son 30 gun',
            altRenk: isDark ? '#888' : '#555',
          },
        ].map((k,i) => (
          <div key={i} style={{ background: cardAlt, borderRadius:10, padding:'16px 18px', border:`1px solid ${border}` }}>
            <div style={{ fontSize:12, color: textSec, marginBottom:6 }}>{k.label}</div>
            <div style={{ fontSize:28, fontWeight:700, color: textPrimary, marginBottom:4 }}>{k.value}</div>
            <div style={{ fontSize:12, color:k.altRenk }}>{k.alt}</div>
          </div>
        ))}
      </div>

      {/* CRM Alarmları */}
      {alarmlar.length > 0 && (
        <div style={{ background: card, border: `1px solid ${isDark ? '#3a3000' : '#FFE58F'}`, borderRadius: 10, padding: 18, marginBottom: 16 }}>
          <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8, color: textPrimary }}>
            🔔 Hatırlatıcılar
            <span style={{ background: isDark ? 'rgba(245,158,11,0.15)' : '#FAEEDA', color: isDark ? '#f5a623' : '#854F0B', borderRadius: 20, padding: '1px 8px', fontSize: 11, fontWeight: 700 }}>{alarmlar.length}</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {alarmlar.map((al: any) => {
              const bugun = new Date().toISOString().slice(0, 10);
              const gecti = al.tarih < bugun;
              const bugunMu = al.tarih === bugun;
              return (
                <div key={al.id} style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', borderRadius: 8,
                  background: gecti ? (isDark ? 'rgba(228,75,74,0.1)' : '#FFF3F3') : bugunMu ? (isDark ? 'rgba(245,158,11,0.1)' : '#FFFBEA') : cardHover,
                  border: `1px solid ${gecti ? (isDark ? 'rgba(228,75,74,0.25)' : '#FACACA') : bugunMu ? (isDark ? 'rgba(245,158,11,0.3)' : '#FFE58F') : border}`,
                }}>
                  <div style={{ flex: 1, minWidth: 0, cursor: 'pointer' }} onClick={() => navigate(`/crm/${al.aday_id}`)}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: isDark ? '#e0e0e0' : '#333' }}>{al.baslik}</span>
                      {gecti && <span style={{ background: isDark ? 'rgba(228,75,74,0.15)' : '#FCEBEB', color: '#A32D2D', borderRadius: 20, padding: '1px 7px', fontSize: 10, fontWeight: 700 }}>Geçti!</span>}
                      {bugunMu && <span style={{ background: isDark ? 'rgba(245,158,11,0.15)' : '#FFF3CD', color: isDark ? '#f5a623' : '#856404', borderRadius: 20, padding: '1px 7px', fontSize: 10, fontWeight: 700 }}>Bugün</span>}
                    </div>
                    <div style={{ fontSize: 11, color: textSec, marginTop: 2 }}>
                      {al.ad} {al.soyad}
                      {' · '}
                      {new Date(al.tarih + 'T00:00:00').toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })}
                      {al.saat && ` · ${al.saat.slice(0, 5)}`}
                      {al.notlar && ` · ${al.notlar}`}
                    </div>
                  </div>
                  <button onClick={() => alarmTamamla(al.id)}
                    style={{ border: '1px solid #1D9E75', background: 'transparent', color: '#1D9E75', borderRadius: 6, padding: '4px 10px', fontSize: 11, fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}>
                    ✓ Tamam
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:16 }}>
        {/* Bugunun dersleri */}
        <div style={{ background: card, border:`1px solid ${border}`, borderRadius:10, padding:18 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
            <div style={{ fontWeight:600, fontSize:14, color: textPrimary }}>Bugunun dersleri</div>
            <span onClick={() => navigate('/program')} style={{ fontSize:12, color:'#534AB7', cursor:'pointer' }}>Programa git →</span>
          </div>
          {!veri?.bugun_dersler?.length ? (
            <div style={{ color: textMuted, fontSize:13, textAlign:'center', padding:20 }}>Bugun ders yok</div>
          ) : (
            veri.bugun_dersler.map((d: any, i: number) => {
              const doluluk = Math.round((d.kayitli / d.kapasite) * 100);
              const dolu = d.kayitli >= d.kapasite;
              return (
                <div key={d.id} onClick={() => navigate(`/dersler/${d.id}`)} style={{ display:'flex', alignItems:'center', gap:12, padding:'10px 0', borderBottom: i < veri.bugun_dersler.length-1 ? `1px solid ${borderSub}` : 'none', cursor:'pointer' }}
                  onMouseEnter={e => (e.currentTarget.style.background = cardHover)}
                  onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                >
                  <div style={{ width:8, height:8, borderRadius:'50%', background:RENKLER[i%RENKLER.length], flexShrink:0 }} />
                  <div style={{ flex:1 }}>
                    <div style={{ fontWeight:500, fontSize:13, color: textPrimary }}>{d.ad}</div>
                    <div style={{ fontSize:11, color: textMuted, marginTop:1 }}>
                      {String(d.baslangic_saat).slice(0,5)} — {String(d.bitis_saat).slice(0,5)} · {d.salon_adi}
                      {d.egitmenler?.[0] ? ` · ${d.egitmenler[0]}` : ''}
                    </div>
                  </div>
                  <span style={{
                    background: dolu ? (isDark ? 'rgba(228,75,74,0.15)' : '#FCEBEB') : doluluk > 80 ? (isDark ? 'rgba(245,158,11,0.15)' : '#FAEEDA') : (isDark ? 'rgba(29,158,117,0.15)' : '#EAF3DE'),
                    color: dolu ? '#A32D2D' : doluluk > 80 ? (isDark ? '#f5a623' : '#854F0B') : '#3B6D11',
                    borderRadius:20, padding:'2px 10px', fontSize:12, fontWeight:500, flexShrink:0
                  }}>
                    {d.kayitli}/{d.kapasite}
                  </span>
                </div>
              );
            })
          )}
        </div>

        {/* Takvim */}
        <div style={{ background: card, border:`1px solid ${border}`, borderRadius:10, padding:18 }}>
          <div style={{ fontWeight:600, fontSize:14, marginBottom:14, textTransform:'capitalize', color: textPrimary }}>
            {simdi.toLocaleDateString('tr-TR', { month:'long', year:'numeric' })} takvimi
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:2, marginBottom:6 }}>
            {GUNLER.map(g => (
              <div key={g} style={{ textAlign:'center', fontSize:10, color: textMuted, fontWeight:500, padding:'4px 0' }}>{g}</div>
            ))}
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:2 }}>
            {Array.from({ length: (ayinIlkGunu + 6) % 7 }).map((_, i) => <div key={`b${i}`} />)}
            {Array.from({ length: ayinSonGunu }).map((_, i) => {
              const gun = i + 1;
              const bugun = gun === bugunGun;
              const yoklamaVar = yoklamaGunleri.has(gun);
              return (
                <div key={gun}
                  onClick={() => gunTikla(gun, bugunAy, bugunYil)}
                  style={{
                    textAlign:'center', padding:'6px 0', borderRadius:6, fontSize:12,
                    background: bugun ? '#534AB7' : yoklamaVar ? (isDark ? 'rgba(83,74,183,0.2)' : '#EEEDFE') : 'transparent',
                    color: bugun ? 'white' : yoklamaVar ? '#9d98e8' : (isDark ? '#ccc' : '#333'),
                    fontWeight: bugun ? 700 : 400,
                    cursor: 'pointer',
                  }}
                  onMouseEnter={e => { if (!bugun) e.currentTarget.style.background = isDark ? '#2a2a2a' : '#f0f0f0'; }}
                  onMouseLeave={e => { if (!bugun) e.currentTarget.style.background = yoklamaVar ? (isDark ? 'rgba(83,74,183,0.2)' : '#EEEDFE') : 'transparent'; }}
                >
                  {gun}
                </div>
              );
            })}
          </div>
          <div style={{ display:'flex', gap:12, marginTop:12, fontSize:11, color: textSec }}>
            <div style={{ display:'flex', alignItems:'center', gap:4 }}>
              <div style={{ width:10, height:10, borderRadius:2, background:'#534AB7' }} /> Bugun
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:4 }}>
              <div style={{ width:10, height:10, borderRadius:2, background: isDark ? 'rgba(83,74,183,0.3)' : '#EEEDFE' }} /> Yoklama alindi
            </div>
          </div>
        </div>
      </div>

      {/* Ödeme Alarmları */}
      {(veri?.odeme_alarmlari?.length > 0) && (
        <div style={{ background: card, border:`1px solid ${isDark ? '#3a2e00' : '#FBBF24'}`, borderRadius:10, padding:18, marginBottom:16 }}>
          <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:14 }}>
            <span style={{ fontSize:16 }}>⚠️</span>
            <div style={{ fontWeight:600, fontSize:14, color: textPrimary }}>Ödeme Alarmları</div>
            <span style={{ background: isDark ? 'rgba(245,158,11,0.15)' : '#FEF3C7', color: isDark ? '#f5a623' : '#92400E', borderRadius:20, padding:'1px 8px', fontSize:11, fontWeight:700 }}>
              {veri.odeme_alarmlari.length} öğrenci
            </span>
            <span style={{ fontSize:12, color: textSec, marginLeft:4 }}>— bugün dersi olan, hakkı bitmiş</span>
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
            {veri.odeme_alarmlari.map((o: any, i: number) => (
              <div key={i}
                onClick={() => navigate(`/ogrenciler/${o.id}`)}
                style={{ display:'flex', alignItems:'center', gap:12, padding:'8px 10px', borderRadius:8, background: isDark ? 'rgba(245,158,11,0.08)' : '#FFFBEB', cursor:'pointer', transition:'background .15s' }}
                onMouseEnter={e => (e.currentTarget.style.background = isDark ? 'rgba(245,158,11,0.14)' : '#FEF3C7')}
                onMouseLeave={e => (e.currentTarget.style.background = isDark ? 'rgba(245,158,11,0.08)' : '#FFFBEB')}
              >
                {o.foto_url
                  ? <img src={fotoUrl(o.foto_url)} alt="" style={{ width:32, height:32, borderRadius:'50%', objectFit:'cover', flexShrink:0 }} />
                  : <div style={{ width:32, height:32, borderRadius:'50%', background: isDark ? 'rgba(245,158,11,0.15)' : '#FEF3C7', display:'flex', alignItems:'center', justifyContent:'center', fontSize:13, fontWeight:700, color:'#92400E', flexShrink:0 }}>
                      {o.ad?.charAt(0)}
                    </div>
                }
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontWeight:600, fontSize:13, color: textPrimary }}>{o.ad} {o.soyad}</div>
                  <div style={{ fontSize:11, color: textSec, marginTop:1 }}>{o.ders_adi}</div>
                </div>
                {o.tel && <span style={{ fontSize:12, color: textSec }}>{o.tel}</span>}
                <span style={{ background: isDark ? 'rgba(228,75,74,0.15)' : '#FCEBEB', color: '#A32D2D', borderRadius:20, padding:'2px 10px', fontSize:11, fontWeight:700, flexShrink:0 }}>
                  Hak bitti
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
        {/* Katılım Teyidi Widget */}
        <div style={{ background: card, border:`1px solid ${border}`, borderRadius:10, padding:18 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
            <div style={{ fontWeight:600, fontSize:14, color: textPrimary }}>Katılım Teyidi</div>
            <span onClick={() => navigate('/katilim')} style={{ fontSize:12, color:'#534AB7', cursor:'pointer' }}>Tümünü gör →</span>
          </div>
          {!teyidler.length ? (
            <div style={{ color: textMuted, fontSize:13, textAlign:'center', padding:20 }}>
              Yaklaşan ders için teyid gönderilmemiş
            </div>
          ) : (
            teyidler.map((t: any, i: number) => {
              const toplam = Number(t.toplam);
              const evet = Number(t.evet);
              const hayir = Number(t.hayir);
              const cevapsiz = Number(t.cevapsiz);
              const tarihStr = new Date(t.tarih).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', weekday: 'short' });
              const saat = t.baslangic_saat ? String(t.baslangic_saat).slice(0, 5) : '';
              return (
                <div key={i}
                  onClick={() => teyidDetayAc(t)}
                  style={{ padding:'10px 8px', borderBottom: i < teyidler.length - 1 ? `1px solid ${borderSub}` : 'none', cursor:'pointer', borderRadius:6, margin:'0 -8px' }}
                  onMouseEnter={e => (e.currentTarget.style.background = cardHover)}
                  onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                >
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:6 }}>
                    <div>
                      <div style={{ fontSize:13, fontWeight:600, color: textPrimary }}>{t.ders_adi}</div>
                      <div style={{ fontSize:11, color: textMuted, marginTop:1 }}>
                        {tarihStr}{saat ? ` · ${saat}` : ''}{t.salon_adi ? ` · ${t.salon_adi}` : ''}
                      </div>
                    </div>
                    <div style={{ fontSize:11, color: textSec, flexShrink:0, marginLeft:8 }}>{toplam} kişi</div>
                  </div>
                  <div style={{ display:'flex', gap:6, alignItems:'center' }}>
                    <span style={{ background: isDark ? 'rgba(29,158,117,0.15)' : '#EAF3DE', color:'#3B6D11', borderRadius:20, padding:'2px 8px', fontSize:11, fontWeight:600 }}>✓ {evet}</span>
                    <span style={{ background: isDark ? 'rgba(228,75,74,0.15)' : '#FCEBEB', color:'#A32D2D', borderRadius:20, padding:'2px 8px', fontSize:11, fontWeight:600 }}>✗ {hayir}</span>
                    <span style={{ background: isDark ? '#2a2a2a' : '#f5f5f5', color: textSec, borderRadius:20, padding:'2px 8px', fontSize:11 }}>? {cevapsiz}</span>
                    <div style={{ flex:1, height:4, background: progressBg2, borderRadius:2, overflow:'hidden', marginLeft:4 }}>
                      {toplam > 0 && (
                        <>
                          <div style={{ float:'left', height:'100%', background:'#1D9E75', width:`${evet/toplam*100}%` }} />
                          <div style={{ float:'left', height:'100%', background:'#E24B4A', width:`${hayir/toplam*100}%` }} />
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Son odemeler */}
        <div style={{ background: card, border:`1px solid ${border}`, borderRadius:10, padding:18 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
            <div style={{ fontWeight:600, fontSize:14, color: textPrimary }}>Son ödemeler</div>
          </div>
          {!veri?.son_odemeler?.length ? (
            <div style={{ color: textMuted, fontSize:13, textAlign:'center', padding:20 }}>Henüz ödeme yok</div>
          ) : (
            veri.son_odemeler.map((o: any, i: number) => (
              <div key={i} onClick={() => navigate(`/ogrenciler/${o.ogrenci_id}`)} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 6px', borderBottom: i < veri.son_odemeler.length-1 ? `1px solid ${borderSub}` : 'none', cursor:'pointer', borderRadius:6, margin:'0 -6px' }}
                onMouseEnter={e => (e.currentTarget.style.background = cardHover)}
                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
              >
                <div>
                  <div style={{ fontWeight:500, fontSize:13, color: textPrimary }}>{o.ogrenci_adi}</div>
                  <div style={{ fontSize:11, color: textMuted, marginTop:1 }}>
                    {new Date(o.olusturuldu).toLocaleDateString('tr-TR', { day:'numeric', month:'short', hour:'2-digit', minute:'2-digit' })}
                  </div>
                </div>
                <span style={{ background: isDark ? 'rgba(29,158,117,0.15)' : '#EAF3DE', color:'#3B6D11', borderRadius:20, padding:'3px 10px', fontSize:12, fontWeight:600 }}>
                  +₺{parseFloat(o.tutar).toLocaleString('tr-TR')}
                </span>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Katılım Teyidi Detay Modalı */}
      {teyidDetay && (
        <>
          <div onClick={() => setTeyidDetay(null)}
            style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.5)', zIndex:150 }} />
          <div style={{ position:'fixed', top:'50%', left:'50%', transform:'translate(-50%,-50%)', background: card, borderRadius:14, width:440, maxHeight:'82vh', overflow:'hidden', display:'flex', flexDirection:'column', zIndex:151, boxShadow:'0 8px 40px rgba(0,0,0,.4)' }}>
            <div style={{ padding:'18px 20px 14px', borderBottom:`1px solid ${border}`, display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
              <div style={{ fontSize:15, fontWeight:700, color: textPrimary }}>{teyidDetay.baslik}</div>
              <button onClick={() => setTeyidDetay(null)} style={{ background:'none', border:'none', cursor:'pointer', fontSize:20, color: textMuted, lineHeight:1, padding:'0 0 0 12px' }}>×</button>
            </div>

            <div style={{ overflowY:'auto', flex:1, padding:'14px 20px 20px' }}>
              {teyidDetayYukleniyor ? (
                <div style={{ textAlign:'center', color: textMuted, fontSize:13, padding:30 }}>Yükleniyor...</div>
              ) : teyidDetay.liste.length === 0 ? (
                <div style={{ textAlign:'center', color: textMuted, fontSize:13, padding:30 }}>Veri bulunamadı</div>
              ) : (() => {
                const evet   = teyidDetay.liste.filter(o => o.cevap === 'evet');
                const hayir  = teyidDetay.liste.filter(o => o.cevap === 'hayir');
                const cevapsiz = teyidDetay.liste.filter(o => o.cevap === 'cevapsiz');

                const OgrenciSatir = ({ o }: { o: any }) => (
                  <div style={{ display:'flex', alignItems:'center', gap:10, padding:'7px 10px', borderRadius:8, background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,.02)', marginBottom:4 }}>
                    <div style={{ width:30, height:30, borderRadius:'50%', background: o.cinsiyet==='kadin' ? '#FBEAF0' : o.cinsiyet==='erkek' ? '#E6F1FB' : '#EEEDFE', color: o.cinsiyet==='kadin' ? '#72243E' : o.cinsiyet==='erkek' ? '#0C447C' : '#534AB7', display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, fontWeight:700, flexShrink:0 }}>
                      {o.ad?.charAt(0)}{o.soyad?.charAt(0)}
                    </div>
                    <span style={{ flex:1, fontSize:13, color: isDark ? '#e0e0e0' : '#333' }}>{o.ad} {o.soyad}</span>
                    {o.cevap_zamani && (
                      <span style={{ fontSize:11, color: textMuted }}>
                        {new Date(o.cevap_zamani).toLocaleTimeString('tr-TR', { hour:'2-digit', minute:'2-digit' })}
                      </span>
                    )}
                  </div>
                );

                return (
                  <>
                    {evet.length > 0 && (
                      <div style={{ marginBottom:16 }}>
                        <div style={{ fontSize:11, fontWeight:700, color:'#3B6D11', marginBottom:8, letterSpacing:.5 }}>
                          ✓ GELİYORUM ({evet.length})
                        </div>
                        {evet.map(o => <OgrenciSatir key={o.ogrenci_id} o={o} />)}
                      </div>
                    )}
                    {hayir.length > 0 && (
                      <div style={{ marginBottom:16 }}>
                        <div style={{ fontSize:11, fontWeight:700, color:'#A32D2D', marginBottom:8, letterSpacing:.5 }}>
                          ✗ GELEMİYORUM ({hayir.length})
                        </div>
                        {hayir.map(o => <OgrenciSatir key={o.ogrenci_id} o={o} />)}
                      </div>
                    )}
                    {cevapsiz.length > 0 && (
                      <div>
                        <div style={{ fontSize:11, fontWeight:700, color: textSec, marginBottom:8, letterSpacing:.5 }}>
                          ? CEVAP VERMEDİ ({cevapsiz.length})
                        </div>
                        {cevapsiz.map(o => <OgrenciSatir key={o.ogrenci_id} o={o} />)}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        </>
      )}

      {/* Gün popup */}
      {gunPopup && (
        <>
          <div onClick={() => setGunPopup(null)}
            style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.4)', zIndex:150 }} />
          <div style={{ position:'fixed', top:'50%', left:'50%', transform:'translate(-50%,-50%)', background: card, borderRadius:12, padding:24, width:360, maxHeight:'80vh', overflowY:'auto', zIndex:151, boxShadow:'0 8px 32px rgba(0,0,0,.4)' }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16 }}>
              <div>
                <div style={{ fontSize:15, fontWeight:700, color: textPrimary }}>{gunPopup.label}</div>
              </div>
              <button onClick={() => setGunPopup(null)}
                style={{ background:'none', border:'none', cursor:'pointer', fontSize:18, color: textSec, padding:0 }}>×</button>
            </div>
            {gunYukleniyor
              ? <div style={{ textAlign:'center', color: textMuted, fontSize:13, padding:20 }}>Yükleniyor...</div>
              : gunDersler.length === 0
                ? <div style={{ textAlign:'center', color: textMuted, fontSize:13, padding:20 }}>Bu gün için ders yok.</div>
                : gunDersler.map((d: any) => {
                    const doluluk = d.kapasite > 0 ? Math.round(d.kayitli / d.kapasite * 100) : 0;
                    return (
                      <div key={d.id} onClick={() => { setGunPopup(null); navigate(`/dersler/${d.id}`); }}
                        style={{ padding:'10px 12px', borderRadius:8, marginBottom:8, background: cardHover, cursor:'pointer', border:`1px solid ${border}` }}
                        onMouseEnter={e => (e.currentTarget.style.background = isDark ? 'rgba(83,74,183,0.15)' : '#EEEDFE')}
                        onMouseLeave={e => (e.currentTarget.style.background = cardHover)}
                      >
                        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:4 }}>
                          <span style={{ fontSize:13, fontWeight:600, color: textPrimary }}>{d.ad}</span>
                          <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                            {d.yoklama_alindi && <span style={{ background: isDark ? 'rgba(29,158,117,0.15)' : '#EAF3DE', color:'#3B6D11', borderRadius:20, padding:'1px 7px', fontSize:10, fontWeight:600 }}>✓ Yoklama</span>}
                            <span style={{ fontSize:12, color: textSec }}>{d.kayitli}/{d.kapasite}</span>
                          </div>
                        </div>
                        <div style={{ fontSize:11, color: textSec }}>
                          {String(d.baslangic_saat).slice(0,5)}–{String(d.bitis_saat).slice(0,5)}
                          {d.salon_adi ? ` · ${d.salon_adi}` : ''}
                          {d.egitmenler?.[0] ? ` · ${d.egitmenler[0]}` : ''}
                        </div>
                        <div style={{ marginTop:6, background: progressBg, borderRadius:3, height:4 }}>
                          <div style={{ height:'100%', borderRadius:3, background: doluluk >= 90 ? '#e24b4a' : doluluk >= 70 ? '#F5A623' : '#1D9E75', width:`${doluluk}%` }} />
                        </div>
                      </div>
                    );
                  })
            }
          </div>
        </>
      )}
    </div>
  );
};

export default Dashboard;
